<template>
  <div>
    <b-breadcrumb class="breadcrumb-pipes">
      <b-breadcrumb-item href="/settings">
        Admin Panel
      </b-breadcrumb-item>
      <b-breadcrumb-item active>
        SFC Prop Accounts
      </b-breadcrumb-item>
    </b-breadcrumb>

    <b-overlay :show="loading" rounded="sm" opacity="0.6" no-fade>
      <b-card class="mt-2">
        <template #header>
          <div class="d-flex justify-content-between align-items-center" style="width:100%">
            <h4 class="mb-0">SFC Prop Accounts</h4>
            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              variant="primary"
              size="sm"
              :disabled="saving"
              @click="saveDetails"
            >
              <b-spinner v-if="saving" small class="mr-50" />
              <feather-icon v-else icon="SaveIcon" class="mr-50" />
              <span class="align-middle">Save</span>
            </b-button>
          </div>
        </template>

        <b-card-body>
          <p class="text-muted" style="font-size:0.85rem">
            SFC's own account details at each place of settlement. These are
            used by the Intesa SetWeb report (PSET_CTP, TP_COD_CTP, COD_CTP).
          </p>

          <div v-for="detail in details" :key="detail.PlaceOfSettlement">
            <h6 class="mt-1 mb-0">{{ placeLabels[detail.PlaceOfSettlement] }}</h6>
            <b-row>
              <b-col cols="12" xl="3" lg="3" md="6" sm="6">
                <b-form-group label="Swift Code">
                  <b-form-input size="sm" type="text" v-model="detail.SwiftCode" />
                </b-form-group>
              </b-col>
              <b-col cols="12" xl="3" lg="3" md="6" sm="6">
                <b-form-group label="Description">
                  <b-form-input size="sm" type="text" v-model="detail.Description" />
                </b-form-group>
              </b-col>
              <b-col cols="12" xl="2" lg="2" md="6" sm="6">
                <b-form-group label="Safekeeping Account">
                  <b-form-input size="sm" type="text" v-model="detail.SafekeepingAccount" />
                </b-form-group>
              </b-col>
              <b-col cols="12" xl="2" lg="2" md="6" sm="6">
                <b-form-group label="Schema">
                  <b-form-input size="sm" type="text" v-model="detail.SchemaCode" />
                </b-form-group>
              </b-col>
              <b-col cols="12" xl="2" lg="2" md="6" sm="6">
                <b-form-group label="ID Schema">
                  <b-form-input size="sm" type="text" v-model="detail.IdSchema" />
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </b-card-body>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BButton, BFormGroup, BFormInput,
  BOverlay, BSpinner, BBreadcrumb, BBreadcrumbItem,
} from "bootstrap-vue";
import store from "@/store";
import { onUnmounted } from "@vue/composition-api";
import settingsStoreModule from "../../settingsStoreModule";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import Ripple from "vue-ripple-directive";

const SETTINGS_APP_STORE_MODULE_NAME = "settings";

export default {
  components: {
    BCard, BCardBody, BRow, BCol, BButton, BFormGroup, BFormInput,
    BOverlay, BSpinner, BBreadcrumb, BBreadcrumbItem, ToastificationContent,
  },

  directives: {
    Ripple,
  },

  setup() {
    if (!store.hasModule(SETTINGS_APP_STORE_MODULE_NAME)) {
      store.registerModule(SETTINGS_APP_STORE_MODULE_NAME, settingsStoreModule);
    }
    onUnmounted(() => {
      if (store.hasModule(SETTINGS_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(SETTINGS_APP_STORE_MODULE_NAME);
      }
    });
  },

  data() {
    return {
      loading: false,
      saving: false,
      details: this.defaultDetails(),
      placeLabels: {
        EUROCLEAR: "Euroclear",
        DTCY: "Dtcy",
        CLEARSTREAM: "Clearstream",
      },
    };
  },

  mounted() {
    this.getDetails();
  },

  methods: {
    defaultDetails() {
      return ["EUROCLEAR", "DTCY", "CLEARSTREAM"].map((place) => ({
        PlaceOfSettlement: place,
        SwiftCode: null,
        Description: null,
        SafekeepingAccount: null,
        SchemaCode: null,
        IdSchema: null,
      }));
    },

    getDetails() {
      this.loading = true;
      store
        .dispatch("settings/getSfcPropAccounts")
        .then((res) => {
          const rows = res.data.resData || [];
          this.details = this.defaultDetails().map((detail) => {
            const found = rows.find(
              (row) =>
                (row.PlaceOfSettlement || "").toUpperCase() ===
                detail.PlaceOfSettlement
            );
            return found
              ? {
                  PlaceOfSettlement: detail.PlaceOfSettlement,
                  SwiftCode: found.SwiftCode,
                  Description: found.Description,
                  SafekeepingAccount: found.SafekeepingAccount,
                  SchemaCode: found.SchemaCode,
                  IdSchema: found.IdSchema,
                }
              : detail;
          });
          this.loading = false;
        })
        .catch((e) => {
          this.loading = false;
          this.showError(`Error fetching SFC prop accounts: ${e.message}`);
        });
    },

    saveDetails() {
      this.saving = true;
      store
        .dispatch("settings/saveSfcPropAccounts", { details: this.details })
        .then((res) => {
          this.saving = false;
          if (res.data.info === "Pass") {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Success",
                text: "SFC prop accounts saved",
                icon: "CheckIcon",
                variant: "success",
              },
            });
          } else {
            this.showError(res.data.message);
          }
        })
        .catch((e) => {
          this.saving = false;
          this.showError(`Error saving SFC prop accounts: ${e.message}`);
        });
    },

    showError(msg) {
      this.$toast({
        component: ToastificationContent,
        props: { title: "Error", text: msg, icon: "AlertTriangleIcon", variant: "danger" },
      });
    },
  },
};
</script>
